import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, Navigate, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCreditCard, faLock, faSpinner, faArrowLeft, faShieldAlt, faKey, faCoins, faCheck, faMapMarkerAlt, faUser, faPhone, faSearch, faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import { AuroraBackground } from '../ui/aurora-background';
import { subscriptionAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useCredit } from '../../context/CreditContext';
import SEO from '../SEO';
import { seoConfig } from '../../utils/seoData';
import { encryptPaymentInfoHybrid } from '../../utils/encryption';
import { sortedCountryCodes, CountryCode } from '../../utils/countryCodes';

const CREDIT_PACKAGE_BASE = {
  creditAmount: 50,
  price: 149,
};

const CREDIT_PACKAGES = [
  { credits: 50, price: 149 },
  { credits: 100, price: 298 },
  { credits: 150, price: 447 },
  { credits: 200, price: 596 },
  { credits: 300, price: 894 },
  { credits: 500, price: 1490 },
];

const AddCreditPage: React.FC = () => {
  const navigate = useNavigate();
  const { lang } = useParams<{ lang: string }>();
  // @ts-ignore - react-i18next supports namespace parameter but TypeScript types don't reflect it
  const { t } = useTranslation('addCredit');
  // @ts-ignore - react-i18next supports namespace parameter but TypeScript types don't reflect it
  const { t: tCommon } = useTranslation('common');
  const { business, refreshBusinessProfile } = useAuth();
  const { refreshCredit } = useCredit();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [publicKey, setPublicKey] = useState<string | null>(null);
  const [selectedPackage, setSelectedPackage] = useState(CREDIT_PACKAGES[0]);
  const [subscription, setSubscription] = useState<any>(null);
  const [creditError, setCreditError] = useState<{
    currentBalance: number;
    estimatedCreditCost: number;
    required: number;
  } | null>(null);
  const [selectedCountry, setSelectedCountry] = useState<CountryCode>(sortedCountryCodes.find(c => c.code === 'TR') || sortedCountryCodes[0]);
  const [countryDropdownOpen, setCountryDropdownOpen] = useState(false);
  const [countrySearchQuery, setCountrySearchQuery] = useState('');
  const countryRef = useRef<HTMLDivElement | null>(null);
  const [fieldErrors, setFieldErrors] = useState<{
    phoneNumber?: string;
    cardHolderName?: string;
    address?: string;
  }>({});
  
  // Phone number state
  const [phoneNumber, setPhoneNumber] = useState('');
  
  // Address information
  const [addressInfo, setAddressInfo] = useState({
    address: '',
    city: 'Istanbul',
    country: 'Turkey',
    zipCode: '34000',
    contactName: '',
  });
  
  // Card info (user info will come from subscription)
  const [cardInfo, setCardInfo] = useState({
    cardHolderName: '',
    cardNumber: '',
    expireMonth: '',
    expireYear: '',
    cvc: '',
    registerConsumerCard: false,
  });

  // Filter countries based on search query
  const filteredCountries = useMemo(() => {
    if (!countrySearchQuery.trim()) {
      return sortedCountryCodes;
    }
    const query = countrySearchQuery.toLowerCase();
    return sortedCountryCodes.filter(country => 
      country.name.toLowerCase().includes(query) ||
      country.nameEn.toLowerCase().includes(query) ||
      country.code.toLowerCase().includes(query) ||
      country.dialCode.includes(query)
    );
  }, [countrySearchQuery]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (countryRef.current && !countryRef.current.contains(event.target as Node)) {
        setCountryDropdownOpen(false);
      }
    };
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setCountryDropdownOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('click', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  useEffect(() => {
    // Check for credit error from sessionStorage
    const creditErrorData = sessionStorage.getItem('creditError');
    if (creditErrorData) {
      try {
        const parsed = JSON.parse(creditErrorData);
        setCreditError(parsed);
        // Clear sessionStorage after reading
        sessionStorage.removeItem('creditError');
        
        // Auto-select a package that covers the required amount
        const required = parsed.required || parsed.estimatedCreditCost || 0;
        const suitablePackage = CREDIT_PACKAGES.find(pkg => pkg.credits >= required) || CREDIT_PACKAGES[CREDIT_PACKAGES.length - 1];
        if (suitablePackage) {
          setSelectedPackage(suitablePackage);
        }
      } catch (err) {
        console.error('Failed to parse credit error data:', err);
      }
    }

    // Get public key for encryption
    const fetchPublicKey = async () => {
      try {
        const response = await subscriptionAPI.getPublicKey();
        const key = typeof response.data === 'string' ? response.data : response.data?.toString();
        if (key) {
          setPublicKey(key);
        }
      } catch (err) {
        console.error('Could not fetch public key:', err);
        setError(t('publicKeyError'));
      }
    };

    // Get subscription to pre-fill user info
    const fetchSubscription = async () => {
      if (!business?._id) return;
      
      try {
        const response = await subscriptionAPI.getSubscription(business._id);
        if (response.data?.subscription) {
          const sub = response.data.subscription;
          setSubscription(sub);
          // Extract phone number without country code for editing
          const phone = sub.gsmNumber || business.phone || '';
          if (phone) {
            // Try to detect country code from phone
            const match = phone.match(/^(\+\d{1,4})\s*(.+)$/);
            if (match) {
              const dialCode = match[1];
              const phoneWithoutCode = match[2];
              setPhoneNumber(phoneWithoutCode);
              const foundCountry = sortedCountryCodes.find(c => c.dialCode === dialCode);
              if (foundCountry) {
                setSelectedCountry(foundCountry);
              }
            } else {
              // No country code found, use as is
              setPhoneNumber(phone.replace(/^\+\d{1,4}\s*/, ''));
            }
          }
          
          // Pre-fill card holder name from subscription
          if (sub.name && sub.surname) {
            setCardInfo(prev => ({
              ...prev,
              cardHolderName: `${sub.name} ${sub.surname}`,
            }));
          }
          
          // Pre-fill address information from subscription
          // Priority: addressDetails > shippingAddress > billingAddress
          const addressDetails = sub.addressDetails || sub.shippingAddress || sub.billingAddress || {};
          const contactName = sub.shippingAddress?.contactName || 
                             sub.billingAddress?.contactName || 
                             (sub.name && sub.surname ? `${sub.name} ${sub.surname}` : '') || '';
          
          setAddressInfo({
            address: addressDetails.address || '',
            city: addressDetails.city || 'Istanbul',
            country: addressDetails.country || 'Turkey',
            zipCode: addressDetails.zipCode || '34000',
            contactName: contactName,
          });
        }
      } catch (err) {
        console.error('Could not fetch subscription:', err);
      }
    };

    fetchPublicKey();
    fetchSubscription();
  }, [business]);

  // Validation functions
  const validatePhone = (phone: string): string | undefined => {
    if (phone && phone.trim().length > 0) {
      // Remove spaces and special characters for validation
      const cleanedPhone = phone.replace(/\s+/g, '').replace(/[-\/\(\)]/g, '');
      // Check if phone contains only digits
      if (!/^\d+$/.test(cleanedPhone)) {
        return 'Telefon numarası sadece rakamlardan oluşmalıdır';
      }
      // Check minimum length (at least 7 digits, max 15 digits for international format)
      if (cleanedPhone.length < 7) {
        return 'Telefon numarası en az 7 haneli olmalıdır';
      }
      if (cleanedPhone.length > 15) {
        return 'Telefon numarası en fazla 15 haneli olabilir';
      }
    }
    return undefined;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    
    // Clear field error when user starts typing
    if (name === 'phoneNumber' && fieldErrors.phoneNumber) {
      setFieldErrors(prev => ({
        ...prev,
        phoneNumber: undefined
      }));
    } else if (name === 'cardHolderName' && fieldErrors.cardHolderName) {
      setFieldErrors(prev => ({
        ...prev,
        cardHolderName: undefined
      }));
    } else if (name === 'address' && fieldErrors.address) {
      setFieldErrors(prev => ({
        ...prev,
        address: undefined
      }));
    }

    // Real-time validation for phone number
    if (name === 'phoneNumber') {
      const phoneError = validatePhone(value);
      if (phoneError) {
        setFieldErrors(prev => ({
          ...prev,
          phoneNumber: phoneError
        }));
      }
    }
    
    // Check if it's an address field
    if (name in addressInfo) {
      setAddressInfo(prev => ({
        ...prev,
        [name]: value
      }));
    } else if (name === 'phoneNumber') {
      setPhoneNumber(value);
      // Validation is already done above
    } else {
      setCardInfo(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
    setError(null);
  };

  const formatCardNumber = (value: string) => {
    const v = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '');
    const matches = v.match(/\d{4,16}/g);
    const match = matches && matches[0] || '';
    const parts = [];
    for (let i = 0, len = match.length; i < len; i += 4) {
      parts.push(match.substring(i, i + 4));
    }
    if (parts.length) {
      return parts.join(' ');
    } else {
      return v;
    }
  };

  const handleCardNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatCardNumber(e.target.value);
    setCardInfo(prev => ({
      ...prev,
      cardNumber: formatted
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setFieldErrors({});

    if (!business?._id) {
      setError(t('businessNotFound'));
      return;
    }

    if (!subscription) {
      setError(t('subscriptionNotFound'));
      return;
    }

    // Validate phone number
    const errors: typeof fieldErrors = {};
    if (phoneNumber) {
      const phoneError = validatePhone(phoneNumber);
      if (phoneError) errors.phoneNumber = phoneError;
    } else {
      errors.phoneNumber = 'Telefon numarası gereklidir';
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    if (!cardInfo.cardNumber || !cardInfo.expireMonth || !cardInfo.expireYear || !cardInfo.cvc) {
      setError(t('fillCardInfo'));
      return;
    }

    if (!publicKey) {
      setError(t('publicKeyError'));
      return;
    }

    setLoading(true);

    try {
      const cardNumber = cardInfo.cardNumber.replace(/\s/g, '');
      
      // Prepare credit purchase data using form data
      // Backend expects: creditAmount, priceUSD, and other fields
      const creditData = {
        creditAmount: selectedPackage.credits,
        priceUSD: selectedPackage.price,
        name: subscription.name || '',
        surname: subscription.surname || '',
        email: subscription.email || business.email || '',
        gsmNumber: phoneNumber ? `${selectedCountry.dialCode} ${phoneNumber}`.trim() : (subscription.gsmNumber || business.phone || ''),
        identityNumber: subscription.identityNumber || '',
        addressDetails: {
          address: addressInfo.address || '',
          city: addressInfo.city || 'Istanbul',
          country: addressInfo.country || 'Turkey',
          zipCode: addressInfo.zipCode || '34000',
        },
        billingAddress: {
          contactName: addressInfo.contactName || `${subscription.name || ''} ${subscription.surname || ''}`.trim(),
          country: addressInfo.country || 'Turkey',
          city: addressInfo.city || 'Istanbul',
          zipCode: addressInfo.zipCode || '34000',
          address: addressInfo.address || t('addressNotEntered'),
        },
        shippingAddress: {
          contactName: addressInfo.contactName || `${subscription.name || ''} ${subscription.surname || ''}`.trim(),
          country: addressInfo.country || 'Turkey',
          city: addressInfo.city || 'Istanbul',
          zipCode: addressInfo.zipCode || '34000',
          address: addressInfo.address || t('addressNotEntered'),
        },
        cardHolderName: cardInfo.cardHolderName || `${subscription.name} ${subscription.surname}`,
        cardNumber: cardNumber,
        expireMonth: cardInfo.expireMonth,
        expireYear: cardInfo.expireYear,
        cvc: cardInfo.cvc,
        description: `Additional credit purchase - ${selectedPackage.credits} credits`,
        registerConsumerCard: cardInfo.registerConsumerCard,
      };

      // Encrypt payment info
      const encrypted = await encryptPaymentInfoHybrid(creditData, publicKey);

      // Send to API
      const response = await subscriptionAPI.addAdditionalCredit(business._id, encrypted);
      
      // Success - refresh and redirect
      if (response.data) {
        try {
          await refreshBusinessProfile();
          await refreshCredit();
          // Redirect back to previous page or dashboard
          navigate(-1);
        } catch (err) {
          console.error('Failed to refresh profile:', err);
          const currentLang = lang || 'tr';
          navigate(`/${currentLang}/social-media`);
        }
      } else {
        setError(t('creditError'));
      }
    } catch (err: any) {
      console.error('Add credit error:', err);
      // Handle server validation errors
      if (err.response?.data?.errors) {
        const serverErrors = err.response.data.errors;
        const fieldErrorMap: typeof fieldErrors = {};
        
        if (serverErrors.gsmNumber) fieldErrorMap.phoneNumber = serverErrors.gsmNumber;
        if (serverErrors.cardHolderName) fieldErrorMap.cardHolderName = serverErrors.cardHolderName;
        if (serverErrors.address) fieldErrorMap.address = serverErrors.address;
        
        if (Object.keys(fieldErrorMap).length > 0) {
          setFieldErrors(fieldErrorMap);
        } else {
          setError(err.response.data.message || t('creditAddError'));
        }
      } else {
        setError(err.response?.data?.message || err.message || t('creditAddError'));
      }
    } finally {
      setLoading(false);
    }
  };

  // If no business, redirect to login
  if (!business) {
    const currentLang = lang || 'tr';
    return <Navigate to={`/${currentLang}/login`} replace />;
  }

  return (
    <>
      <SEO {...seoConfig.login} />
      <AuroraBackground className="w-full h-full overflow-scroll">
        <div className="w-full min-h-screen flex items-center justify-center relative overflow-hidden py-10">
          {/* Background Effects */}
          <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-purple-900/20 to-gray-900"></div>
          <div className="absolute inset-0 opacity-20" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%239C92AC' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='1'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
          }}></div>

          {/* Floating Elements */}
          <div className="absolute top-20 left-10 w-20 h-20 bg-purple-500/10 rounded-full blur-xl animate-float"></div>
          <div className="absolute top-40 right-20 w-32 h-32 bg-blue-500/10 rounded-full blur-xl animate-float" style={{ animationDelay: '1s' }}></div>
          <div className="absolute bottom-20 left-1/4 w-24 h-24 bg-pink-500/10 rounded-full blur-xl animate-float" style={{ animationDelay: '2s' }}></div>

          <div className="relative z-10 w-full max-w-5xl mx-auto px-6">
            <div className="grid md:grid-cols-2 gap-8">
              {/* Left Column - Credit Packages */}
              <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-3xl p-8 shadow-2xl">
                <button
                  onClick={() => navigate(-1)}
                  className="flex items-center space-x-2 text-white/80 md:hover:text-white transition-colors mb-6"
                >
                  <FontAwesomeIcon icon={faArrowLeft} />
                  <span>{t('back')}</span>
                </button>

                <h2 className="text-2xl font-bold text-white mb-6 flex items-center space-x-2">
                  <FontAwesomeIcon icon={faCoins} className="text-purple-400" />
                  <span>{t('title')}</span>
                </h2>
                
                <div className="mb-6">
                  <p className="text-white/70 mb-4">
                    {t('description')}
                  </p>
                  
                  {/* Credit Packages Grid */}
                  <div className="grid grid-cols-2 gap-3">
                    {CREDIT_PACKAGES.map((pkg) => (
                      <button
                        key={pkg.credits}
                        type="button"
                        onClick={() => setSelectedPackage(pkg)}
                        className={`p-4 rounded-xl border-2 transition-all ${
                          selectedPackage.credits === pkg.credits
                            ? 'border-purple-500 bg-purple-500/20'
                            : 'border-white/10 bg-white/5 md:hover:border-purple-500/50'
                        }`}
                      >
                        <div className="text-center">
                          <div className="text-2xl font-bold text-white mb-1">
                            {pkg.credits}
                          </div>
                          <div className="text-sm text-white/70 mb-2">{t('credits')}</div>
                          <div className="text-lg font-semibold text-purple-400">
                            ${pkg.price}
                          </div>
                          {selectedPackage.credits === pkg.credits && (
                            <div className="mt-2">
                              <FontAwesomeIcon icon={faCheck} className="text-green-400" />
                            </div>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Selected Package Info */}
                <div className="bg-purple-500/10 border border-purple-500/20 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-white/80">{t('selectedPackage')}</span>
                    <span className="text-white font-semibold">{selectedPackage.credits} {t('credits')}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-white/80">{t('total')}</span>
                    <span className="text-2xl font-bold text-purple-400">
                      ${selectedPackage.price}
                    </span>
                  </div>
                </div>
              </div>

              {/* Right Column - Payment Form */}
              <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-3xl p-8 shadow-2xl">
                <h2 className="text-2xl font-bold text-white mb-6">{t('paymentInfo')}</h2>

                {creditError && (
                  <div className="bg-yellow-500/20 border border-yellow-500/30 text-yellow-400 p-4 rounded-lg mb-4">
                    <div className="flex items-start space-x-2">
                      <FontAwesomeIcon icon={faCoins} className="mt-0.5 flex-shrink-0" />
                      <div className="flex-1">
                        <p className="font-semibold mb-2">{t('insufficientCredit')}</p>
                        <div className="text-sm space-y-1">
                          <p>{t('currentBalance')}: <span className="font-medium">{creditError.currentBalance.toFixed(2)}</span></p>
                          <p>{t('requiredLabel')}: <span className="font-medium">{creditError.required.toFixed(2)}</span></p>
                          <p className="text-yellow-300 mt-2">{t('pleaseAddCredit')}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                {error && (
                  <div className="bg-red-500/20 text-red-400 p-3 rounded-lg text-center mb-4">
                    {error}
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Address Information */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-white flex items-center space-x-2">
                      <FontAwesomeIcon icon={faMapMarkerAlt} />
                      <span>{t('addressInfo')}</span>
                    </h3>
                    
                    <div>
                      <label className="block text-sm font-medium text-white/80 mb-2 flex items-center space-x-2">
                        <FontAwesomeIcon icon={faUser} className="text-sm" />
                        <span>{t('contactName')}</span>
                      </label>
                      <input
                        type="text"
                        name="contactName"
                        value={addressInfo.contactName}
                        onChange={handleInputChange}
                        placeholder={`${subscription?.name || ''} ${subscription?.surname || ''}`.trim()}
                        className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-white/80 mb-2 flex items-center space-x-2">
                        <FontAwesomeIcon icon={faPhone} className="text-sm" />
                        <span>{tCommon('phone')} {t('required') || '*'}</span>
                      </label>
                      <div className="flex gap-2">
                        {/* Country Code Selector */}
                        <div className="relative" ref={countryRef}>
                          <button
                            type="button"
                            onClick={() => {
                              setCountryDropdownOpen(!countryDropdownOpen);
                              setCountrySearchQuery('');
                            }}
                            className="flex items-center gap-2 px-3 h-[50px] bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-300 min-w-[100px]"
                          >
                            <span className="text-lg">{selectedCountry.flag}</span>
                            <span className="text-sm font-medium">{selectedCountry.dialCode}</span>
                            <FontAwesomeIcon icon={faChevronDown} className={`text-white/50 text-xs transition-transform ${countryDropdownOpen ? 'rotate-180' : ''}`} />
                          </button>
                          {countryDropdownOpen && (
                            <div className="absolute z-30 mt-2 w-80 bg-gray-900/95 backdrop-blur-xl border border-white/10 rounded-xl shadow-xl overflow-hidden">
                              {/* Search Input */}
                              <div className="p-3 border-b border-white/10">
                                <div className="relative">
                                  <FontAwesomeIcon icon={faSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-white/40 text-sm" />
                                  <input
                                    type="text"
                                    value={countrySearchQuery}
                                    onChange={(e) => setCountrySearchQuery(e.target.value)}
                                    placeholder={tCommon('countryCodeSearchPlaceholder')}
                                    className="w-full pl-10 pr-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
                                    autoFocus
                                  />
                                </div>
                              </div>
                              {/* Country List */}
                              <div className="max-h-64 overflow-y-auto">
                                {filteredCountries.length > 0 ? (
                                  filteredCountries.map((country) => (
                                    <button
                                      key={country.code}
                                      type="button"
                                      onClick={() => {
                                        setSelectedCountry(country);
                                        setCountryDropdownOpen(false);
                                        setCountrySearchQuery('');
                                      }}
                                      className={`w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-white/10 transition-colors ${
                                        selectedCountry.code === country.code ? 'bg-white/10' : ''
                                      }`}
                                    >
                                      <span className="text-xl">{country.flag}</span>
                                      <div className="flex-1 min-w-0">
                                        <div className="text-white text-sm font-medium truncate">{country.name}</div>
                                        <div className="text-white/60 text-xs truncate">{country.nameEn}</div>
                                      </div>
                                      <div className="flex items-center gap-2 text-white/70 text-sm">
                                        <span className="font-medium">{country.dialCode}</span>
                                        <span className="text-xs">({country.code})</span>
                                      </div>
                                    </button>
                                  ))
                                ) : (
                                  <div className="px-4 py-8 text-center text-white/60 text-sm">
                                    {tCommon('countryNotFound')}
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                        {/* Phone Number Input */}
                        <div className="relative flex-1">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <FontAwesomeIcon icon={faPhone} className="text-white/40 text-sm" />
                          </div>
                          <input
                            type="tel"
                            name="phoneNumber"
                            value={phoneNumber}
                            onChange={handleInputChange}
                            placeholder="555 123 4567"
                            className={`w-full pl-10 pr-4 h-[50px] bg-white/5 border rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all ${
                              fieldErrors.phoneNumber ? 'border-red-500/50 focus:ring-red-500' : 'border-white/10'
                            }`}
                            required
                          />
                        </div>
                      </div>
                      {fieldErrors.phoneNumber ? (
                        <p className="mt-1 text-xs text-red-400">{fieldErrors.phoneNumber}</p>
                      ) : (
                        <p className="mt-1 text-xs text-white/50">
                          {tCommon('fullPhoneNumber')}: {selectedCountry.dialCode} {phoneNumber || '...'}
                        </p>
                      )}
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-white/80 mb-2">{t('address')}</label>
                      <input
                        type="text"
                        name="address"
                        value={addressInfo.address}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all"
                      />
                    </div>

                    <div className="grid grid-cols-3 gap-4">
                      <div className="col-span-2">
                        <label className="block text-sm font-medium text-white/80 mb-2">{t('city')}</label>
                        <input
                          type="text"
                          name="city"
                          value={addressInfo.city}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-white/80 mb-2">{t('zipCode')}</label>
                        <input
                          type="text"
                          name="zipCode"
                          value={addressInfo.zipCode}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-white/80 mb-2">{t('country')}</label>
                      <input
                        type="text"
                        name="country"
                        value={addressInfo.country}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all"
                      />
                    </div>
                  </div>

                  {/* Card Information */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-white flex items-center space-x-2">
                      <FontAwesomeIcon icon={faCreditCard} />
                      <span>{t('cardInfo')}</span>
                    </h3>
                    
                    <div>
                      <label className="block text-sm font-medium text-white/80 mb-2">{t('cardHolderName')} {t('required')}</label>
                      <input
                        type="text"
                        name="cardHolderName"
                        value={cardInfo.cardHolderName}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-white/80 mb-2">{t('cardNumber')} {t('required')}</label>
                      <input
                        type="text"
                        name="cardNumber"
                        value={cardInfo.cardNumber}
                        onChange={handleCardNumberChange}
                        placeholder="1234 5678 9012 3456"
                        maxLength={19}
                        className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all"
                        required
                      />
                    </div>

                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-white/80 mb-2">{t('expireMonth')} {t('required')}</label>
                        <select
                          name="expireMonth"
                          value={cardInfo.expireMonth}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all"
                          required
                        >
                          <option value="">{t('month')}</option>
                          {Array.from({ length: 12 }, (_, i) => i + 1).map(month => (
                            <option key={month} value={String(month).padStart(2, '0')}>
                              {String(month).padStart(2, '0')}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-white/80 mb-2">{t('expireYear')} {t('required')}</label>
                        <select
                          name="expireYear"
                          value={cardInfo.expireYear}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all"
                          required
                        >
                          <option value="">{t('year')}</option>
                          {Array.from({ length: 20 }, (_, i) => new Date().getFullYear() + i).map(year => (
                            <option key={year} value={String(year)}>
                              {year}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-white/80 mb-2">{t('cvc')} {t('required')}</label>
                        <input
                          type="text"
                          name="cvc"
                          value={cardInfo.cvc}
                          onChange={handleInputChange}
                          placeholder="123"
                          maxLength={3}
                          className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all"
                          required
                        />
                      </div>
                    </div>

                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        name="registerConsumerCard"
                        id="registerConsumerCard"
                        checked={cardInfo.registerConsumerCard}
                        onChange={handleInputChange}
                        className="w-4 h-4 text-purple-600 bg-white/5 border-white/20 rounded focus:ring-purple-500"
                      />
                      <label htmlFor="registerConsumerCard" className="text-sm text-white/70">
                        {t('saveCard')}
                      </label>
                    </div>
                  </div>

                  {/* Security Information */}
                  <div className="bg-green-500/10 border border-green-500/20 rounded-xl p-4 space-y-3">
                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0 mt-1">
                        <FontAwesomeIcon icon={faShieldAlt} className="text-green-400 text-xl" />
                      </div>
                      <div className="flex-1">
                        <h4 className="text-green-400 font-semibold mb-2 flex items-center space-x-2">
                          <span>{t('securePayment')}</span>
                          <FontAwesomeIcon icon={faLock} className="text-sm" />
                        </h4>
                        <div className="space-y-2 text-sm text-white/80">
                          <div className="flex items-start space-x-2">
                            <FontAwesomeIcon icon={faCheck} className="text-green-400 text-xs mt-1 flex-shrink-0" />
                            <span>{t('encryptionInfo.endToEnd')}</span>
                          </div>
                          <div className="flex items-start space-x-2">
                            <FontAwesomeIcon icon={faCheck} className="text-green-400 text-xs mt-1 flex-shrink-0" />
                            <span>{t('encryptionInfo.ssl')}</span>
                          </div>
                        </div>
                        <div className="mt-3 pt-3 border-t border-green-500/20">
                          <p className="text-xs text-white/60">
                            <FontAwesomeIcon icon={faKey} className="text-green-400 mr-1" />
                            {t('encryptionInfo.browserNote')}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={loading || !publicKey}
                    className="w-full bg-gradient-to-r from-purple-500 to-purple-700 px-8 py-4 rounded-xl text-white font-semibold md:hover:from-purple-600 md:hover:to-purple-800 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                  >
                    {loading ? (
                      <>
                        <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
                        <span>{t('processing')}</span>
                      </>
                    ) : !publicKey ? (
                      <>
                        <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
                        <span>{t('preparingSecurity')}</span>
                      </>
                    ) : (
                      <>
                        <FontAwesomeIcon icon={faLock} />
                        <span>${selectedPackage.price} {t('buyCredits')} {selectedPackage.credits} {t('credits')}</span>
                      </>
                    )}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </AuroraBackground>
    </>
  );
};

export default AddCreditPage;

